import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Autocomplete,
  IconButton,
  Divider
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';

const GoalForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [formData, setFormData] = useState({
    employeeId: null,
    goalTitle: '',
    description: '',
    goalType: 'INDIVIDUAL',
    category: 'PERFORMANCE',
    priority: 'MEDIUM',
    startDate: new Date().toISOString().split('T')[0],
    targetDate: '',
    milestones: [],
    metrics: {
      targetValue: '',
      currentValue: 0,
      unit: ''
    }
  });

  useEffect(() => {
    fetchEmployees();
    if (id) {
      fetchGoal();
    }
  }, [id]);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get('/employee/getAllEmployees');
      if (Array.isArray(response.data)) {
        setEmployees(response.data);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchGoal = async () => {
    try {
      const response = await axios.get(`/api/employee-goal/${id}`);
      if (response.data.success) {
        const goal = response.data.data;
        setFormData({
          ...goal,
          startDate: new Date(goal.startDate).toISOString().split('T')[0],
          targetDate: new Date(goal.targetDate).toISOString().split('T')[0]
        });
      }
    } catch (error) {
      Swal.fire('Error', 'Failed to fetch goal details', 'error');
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleMetricChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      metrics: { ...prev.metrics, [field]: value }
    }));
  };

  const addMilestone = () => {
    setFormData(prev => ({
      ...prev,
      milestones: [
        ...prev.milestones,
        { title: '', description: '', targetDate: '', completed: false }
      ]
    }));
  };

  const updateMilestone = (index, field, value) => {
    const newMilestones = [...formData.milestones];
    newMilestones[index][field] = value;
    setFormData(prev => ({ ...prev, milestones: newMilestones }));
  };

  const removeMilestone = (index) => {
    setFormData(prev => ({
      ...prev,
      milestones: prev.milestones.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.employeeId) {
      Swal.fire('Error', 'Please select an employee', 'error');
      return;
    }
    if (!formData.goalTitle) {
      Swal.fire('Error', 'Please enter goal title', 'error');
      return;
    }
    if (!formData.targetDate) {
      Swal.fire('Error', 'Please select target date', 'error');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        employeeId: formData.employeeId._id || formData.employeeId,
        branchId: formData.employeeId.outletLocation || formData.branchId
      };

      const response = id
        ? await axios.put(`/api/employee-goal/${id}`, payload)
        : await axios.post('/api/employee-goal/create', payload);

      if (response.data.success) {
        Swal.fire('Success', `Goal ${id ? 'updated' : 'created'} successfully`, 'success');
        navigate('/userdashboard/staff/hrm/performance/goals');
      }
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'Failed to save goal', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/userdashboard/staff/hrm/performance/goals')}>
          <BackIcon />
        </IconButton>
        <Typography variant="h5" fontWeight={600}>
          {id ? 'Edit Goal' : 'Create New Goal'}
        </Typography>
      </Box>

      <Paper sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* Employee Selection */}
          <Grid item xs={12}>
            <Autocomplete
              options={employees}
              getOptionLabel={(option) => `${option.staff_name} (${option.emp_id})`}
              value={formData.employeeId}
              onChange={(e, value) => handleChange('employeeId', value)}
              renderInput={(params) => <TextField {...params} label="Select Employee *" />}
              disabled={!!id}
            />
          </Grid>

          {/* Goal Title */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Goal Title *"
              value={formData.goalTitle}
              onChange={(e) => handleChange('goalTitle', e.target.value)}
            />
          </Grid>

          {/* Description */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description *"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
            />
          </Grid>

          {/* Goal Type */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              select
              label="Goal Type"
              value={formData.goalType}
              onChange={(e) => handleChange('goalType', e.target.value)}
            >
              <MenuItem value="INDIVIDUAL">Individual</MenuItem>
              <MenuItem value="TEAM">Team</MenuItem>
              <MenuItem value="DEPARTMENT">Department</MenuItem>
              <MenuItem value="COMPANY">Company</MenuItem>
            </TextField>
          </Grid>

          {/* Category */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              select
              label="Category"
              value={formData.category}
              onChange={(e) => handleChange('category', e.target.value)}
            >
              <MenuItem value="PERFORMANCE">Performance</MenuItem>
              <MenuItem value="SKILL_DEVELOPMENT">Skill Development</MenuItem>
              <MenuItem value="PROJECT">Project</MenuItem>
              <MenuItem value="SALES">Sales</MenuItem>
              <MenuItem value="QUALITY">Quality</MenuItem>
              <MenuItem value="OTHER">Other</MenuItem>
            </TextField>
          </Grid>

          {/* Priority */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              select
              label="Priority"
              value={formData.priority}
              onChange={(e) => handleChange('priority', e.target.value)}
            >
              <MenuItem value="LOW">Low</MenuItem>
              <MenuItem value="MEDIUM">Medium</MenuItem>
              <MenuItem value="HIGH">High</MenuItem>
              <MenuItem value="CRITICAL">Critical</MenuItem>
            </TextField>
          </Grid>

          {/* Start Date */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Start Date"
              value={formData.startDate}
              onChange={(e) => handleChange('startDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Target Date */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Target Date *"
              value={formData.targetDate}
              onChange={(e) => handleChange('targetDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Metrics */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Metrics (Optional)
            </Typography>
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              type="number"
              label="Target Value"
              value={formData.metrics.targetValue}
              onChange={(e) => handleMetricChange('targetValue', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              type="number"
              label="Current Value"
              value={formData.metrics.currentValue}
              onChange={(e) => handleMetricChange('currentValue', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Unit"
              value={formData.metrics.unit}
              onChange={(e) => handleMetricChange('unit', e.target.value)}
              placeholder="e.g., Sales, Hours, Count"
            />
          </Grid>

          {/* Milestones */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Milestones
              </Typography>
              <Button startIcon={<AddIcon />} onClick={addMilestone}>
                Add Milestone
              </Button>
            </Box>
          </Grid>

          {formData.milestones.map((milestone, index) => (
            <Grid item xs={12} key={index}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={5}>
                    <TextField
                      fullWidth
                      label="Milestone Title"
                      value={milestone.title}
                      onChange={(e) => updateMilestone(index, 'title', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Target Date"
                      value={milestone.targetDate}
                      onChange={(e) => updateMilestone(index, 'targetDate', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <IconButton color="error" onClick={() => removeMilestone(index)}>
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      label="Description"
                      value={milestone.description}
                      onChange={(e) => updateMilestone(index, 'description', e.target.value)}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          ))}

          {/* Actions */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/userdashboard/staff/hrm/performance/goals')}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? 'Saving...' : id ? 'Update Goal' : 'Create Goal'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default GoalForm;
